/*
 * <video-reviews-section> — behavior for sections/section-video-reviews.liquid.
 * Exactly one card is always active: the first one on load, then the last one
 * the user interacted with (hover on desktop, tap/toggle on touch). The active
 * card only plays while it is on screen - it starts once scrolled into view,
 * pauses when scrolled away and resumes on return.
 */
(function () {
  if (customElements.get('video-reviews-section')) return;

  customElements.define(
    'video-reviews-section',
    class VideoReviewsSection extends HTMLElement {
      connectedCallback() {
        const cards = Array.from(this.querySelectorAll('[data-video-card]')).filter((card) =>
          card.querySelector('[data-video]')
        );
        if (!cards.length) return;

        this._cleanup = [];
        const on = (target, type, handler, opts) => {
          target.addEventListener(type, handler, opts);
          this._cleanup.push(() => target.removeEventListener(type, handler, opts));
        };

        const canHover = () => matchMedia('(hover: hover) and (pointer: fine)').matches;

        let activeCard = null;
        // ponytail: set when the user pauses the active card via its toggle, so
        // scrolling it back into view doesn't resume it behind their back.
        let userPaused = false;

        const mark = (card, active) => card.classList.toggle('is-playing', active);

        // Sound is one shared state for the whole section. It starts on, and
        // flips off by itself if the browser refuses audible playback (which it
        // does until the visitor has interacted with the page).
        let soundOn = true;
        let userChoseSound = false;
        const soundButtons = Array.from(this.querySelectorAll('[data-sound-toggle]'));
        const syncSound = () =>
          soundButtons.forEach((button) => button.setAttribute('aria-pressed', String(soundOn)));

        const play = (video) => {
          // Sound is the default, so re-arm it on every start until the visitor
          // turns it off themselves.
          if (!soundOn && !userChoseSound) {
            soundOn = true;
            syncSound();
          }
          video.muted = !soundOn;
          video.play().catch(() => {});
          if (!soundOn) return;
          // A refusal shows up as a rejected play() or as the video being paused
          // right after it was unmuted — both leave it paused, so recover muted.
          setTimeout(() => {
            if (video.paused && !userPaused) {
              soundOn = false;
              syncSound();
              video.muted = true;
              video.play().catch(() => {});
            }
          }, 150);
        };

        const setActive = (card) => {
          if (activeCard === card) return;
          if (activeCard) {
            const prev = activeCard.querySelector('[data-video]');
            prev.pause();
            prev.muted = true;
            prev.currentTime = 0;
            mark(activeCard, false);
          }
          activeCard = card;
          userPaused = false;
          mark(card, true);
          play(card.querySelector('[data-video]'));
        };

        // Once the visitor has tapped or typed anywhere, sound is allowed again,
        // so retry it on the playing card — unless they muted it themselves.
        const retrySound = (event) => {
          // The sound button's own click decides the state — don't pre-empt it.
          if (event.target?.closest?.('[data-sound-toggle]')) return;
          const video = activeCard?.querySelector('[data-video]');
          if (soundOn || userChoseSound || !video || video.paused) return;
          soundOn = true;
          syncSound();
          play(video);
        };
        on(document, 'pointerdown', retrySound, { capture: true });
        on(document, 'keydown', retrySound, { capture: true });

        soundButtons.forEach((button) =>
          on(button, 'click', () => {
            soundOn = !soundOn;
            userChoseSound = true;
            syncSound();
            const video = activeCard?.querySelector('[data-video]');
            if (!video) return;
            soundOn ? play(video) : (video.muted = true);
          })
        );
        syncSound();

        cards.forEach((card) => {
          const video = card.querySelector('[data-video]');
          const toggle = card.querySelector('[data-video-toggle]');
          if (toggle) {
            const syncIcon = () => toggle.setAttribute('aria-pressed', String(!video.paused));
            on(video, 'play', syncIcon);
            on(video, 'pause', syncIcon);
            syncIcon();
          }

          on(card, 'mouseenter', () => {
            if (canHover()) setActive(card);
          });
          on(card, 'click', (event) => {
            if (card === activeCard && event.target.closest('[data-video-toggle]')) {
              const video = card.querySelector('[data-video]');
              userPaused = !video.paused;
              // ponytail: `is-playing` stays on so the card keeps its active
              // size/overlay while paused — the icon follows aria-pressed,
              // which the video's own play/pause events keep in sync.
              userPaused ? video.pause() : play(video);
              return;
            }
            setActive(card);
          });
        });

        // Pause the active card while it is scrolled out of view, resume on return.
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.target !== activeCard) return;
              const video = entry.target.querySelector('[data-video]');
              if (entry.isIntersecting && !userPaused) {
                play(video);
              } else {
                video.pause();
                video.muted = true;
              }
            });
          },
          // Half the card inside the middle 70% of the viewport - "in view" means
          // the visitor actually scrolled to it, not that an edge peeks in.
          { threshold: 0.5, rootMargin: '-15% 0px' }
        );
        cards.forEach((card) => observer.observe(card));
        this._cleanup.push(() => observer.disconnect());

        // The first card is active from the start but does not play until the
        // observer above sees it on screen - otherwise the page opens with audio
        // coming from a section the visitor hasn't scrolled to yet.
        activeCard = cards[0];
        mark(activeCard, true);
      }

      disconnectedCallback() {
        this._cleanup?.forEach((fn) => fn());
        this._cleanup = [];
      }
    }
  );
})();
